import { AuthenticationService } from '@shared/http/authentication.service';
import { CoreResponseError } from '@shared/http/core-response-error';
import { CWUser } from 'cw-isdk/core';
import { User } from 'oidc-client-ts';
import { useEffect } from 'react';
import { catchError, forkJoin, of, tap } from 'rxjs';
import Container from 'typedi';
import { LoggingService } from '../logging.service';
import { TidOauthAuthService } from '../tid-oauth-auth.service';
import { useRefStateObject } from './use-ref-state-object.hook';
import { useSymbol } from './use-symbol.hook';

const logger = Container.get(LoggingService);

export function useCurrentUser(): UseCurrentUserState {

    const [state, setState] = useRefStateObject<UseCurrentUserState>({
        loading: false,
        user: undefined,
        tidUser: undefined,
        reload: () => setReloadSymbol()
    });

    const [reloadSymbol, setReloadSymbol] = useSymbol(Symbol());

    useEffect(() => {
        setState({ loading: true });
        const authenticationService = Container.get(AuthenticationService);
        const oauthAuthSvc = Container.get(TidOauthAuthService);

        const sub = forkJoin({
            authGetUser: authenticationService.getUser({}),
            initOauthAuthSvc: oauthAuthSvc.initUserManagerFromStorage()
        }).pipe(
            tap(async (response) => {
                const user = (response.authGetUser?.EmployeeSid ?? 0) < 1 ? undefined : response.authGetUser;
                let tidUser = await oauthAuthSvc?.getUser();
                if (!tidUser || !tidUser.access_token) {
                    tidUser = undefined;
                }
                oauthAuthSvc.logExpireDate('user hook', tidUser);

                setState({
                    loading: false,
                    user,
                    tidUser
                });
            }),
            catchError(error => {
                if (!(error instanceof CoreResponseError && error.coreResponse?.Status === 3)) {
                    logger.error(error);
                }
                setState({ loading: false, user: undefined, tidUser: undefined });
                return of();
            })
        ).subscribe();

        return () => {
            setState({ loading: false });
            sub.unsubscribe();
        };
    }, [reloadSymbol]);

    return state.current;
}

export interface UseCurrentUserState {
    loading: boolean;
    reload: () => void;
    user: CWUser | undefined;
    tidUser: User | undefined;
}
